import React, { useEffect, useState, useRef } from 'react';
import { Button, Divider, Typography } from '@material-ui/core';
import { useLibrary } from '../hooks/useLibrary';
import { useTemplates } from '../hooks/useTemplates';
import { EeInput } from 'components/EeInput/EeInput';
import { EeSelect } from 'components/EeSelect/EeSelect';
import { TemplatesTable } from './TemplatesTable';
import { KPITemplatesDrawer, DRAWER_MODE_TITLES } from './KPITemplatesDrawer';
import { observer } from 'mobx-react-lite';
import { EeDrawer } from 'components/EeDrawer/EeDrawer';
import { EeModal } from 'components/EeModal/EeModal';
import { useCompany } from '../hooks/useCompany';
import { useLocation } from 'react-router-dom';
import { useSnackbar } from 'hooks/useSnackbar';

export const TemplatesPage = observer(() => {
  const usingLibrary = useLibrary();
  const usingCompany = useCompany();
  const usingTemplates = useTemplates();
  const [mode, setMode] = useState('new');
  const [openCloseConfirmation, setOpenCloseConfirmation] = useState(false);
  const { addSnackbar } = useSnackbar();
  const templatesTableRef = useRef();
  const location = useLocation<{ templateId: string }>();

  const [companiesByTemplateId, setCompaniesByTemplateId] = useState<any>({});
  const handleSave = async ({ mode, data }: any) => {
    const result = await usingTemplates.operations.saveTemplate({ mode, data });
    if (result.errorCode === 0) {
      usingTemplates.operations.loadTemplates();
      const content =
        mode === 'edit'
          ? `Template Updated: ${data.name} was successfully saved.`
          : `New Template: ${data.name} was successfully created.`;

      addSnackbar(content);
      if (mode === 'new') {
        setTimeout(() => {
          // @ts-ignore
          const [firstRow] = templatesTableRef.current.rows;
          if (!firstRow?.isExpanded)
            // @ts-ignore
            templatesTableRef.current.toggleRowExpanded('0');
        }, 1000);
      }
    } else {
      addSnackbar('Failed to create Template');
    }
    usingTemplates.operations.closeKPIDrawer();
  };

  useEffect(() => {
    const loadData = async () => {
      await usingLibrary.operations.loadKPIs();
      usingLibrary.operations.loadTypes();
      usingTemplates.operations.loadTemplates();
      usingCompany.operations.loadCompanies();

      if (!(location.state && location.state.templateId))
        // @ts-ignore
        templatesTableRef.current.toggleRowExpanded('0');
    };
    loadData().then();
  }, []);

  useEffect(() => {
    const { companies } = usingCompany.models;
    const resultMap = {};
    for (let i = 0; i < companies.length; i++) {
      let company = companies[i];
      if (company.templateId === null) continue;
      // @ts-ignore
      if (!resultMap[company.templateId]) resultMap[company.templateId] = [];
      // @ts-ignore
      resultMap[company.templateId].push(company);
    }
    setCompaniesByTemplateId(resultMap);
  }, [usingCompany.models.companies]);
  return (
    <div className="bg-gray w-100 mx-auto px-3" style={{ maxWidth: 1200 }}>
      <Typography variant="h1" className="my-3">
        KPI Templates
      </Typography>
      <div className="d-flex flex-wrap lens-align-bottom">
        <EeInput
          value={usingTemplates.models.search.value}
          search
          onChange={usingTemplates.operations.handleSearch}
          type="search"
          className="mr-3"
          notFullWidth
          placeholder="Search"
        />
        <div className="d-flex align-items-center flex-grow-1 mr-3 lens-input-warper lens-ml-2 lens-align-start">
          <span className="mr-2 lens-ee-label">Filter by</span>
          <EeSelect
            items={[
              { id: 'All Templates', name: 'All Templates' },
              // @ts-ignore
              ...usingTemplates.models.templateOptions,
            ]}
            value={usingTemplates.models.search.filterId}
            onChange={usingTemplates.operations.handleGroupsChange}
          />
        </div>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setMode('new');
            usingTemplates.operations.openNewKPIDrawer();
          }}
        >
          create new template
        </Button>
      </div>
      <Divider />
      <TemplatesTable
        // @ts-ignore
        ref={templatesTableRef}
        companiesByTemplateId={companiesByTemplateId}
        templateRows={usingTemplates.models.templates
          .sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()))
          .map((t) => {
            const subRows = t.indicators
              ?.map(({ id: kpiId }: any) => usingLibrary.models.kpisByIds[kpiId])
              .filter((kpi) => kpi);
            const expanded = location.state && location.state.templateId === t.id;
            return {
              ...t,
              expanded:
                expanded ||
                (usingTemplates.models.search.value &&
                usingTemplates.models.templates.length === 1
                  ?
                    // @ts-ignore
                    t.expanded || true
                  : undefined),
              subRows: subRows?.sort(
                (a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())
              ),
            };
          })
        }
        onRowEdit={({ row }) => {
          setMode('edit');
          usingTemplates.operations.openEditKPIDrawer({ row });
        }}
        onRowDuplicate={({ row }) => {
          setMode('duplicate');
          usingTemplates.operations.duplicateTemplate({ row });
        }}
        onRowDelete={async ({ row }) => {
          const response = await usingTemplates.operations.deleteTemplate({
            row,
          });

          if (response.errorCode === 0) {
            setOpenCloseConfirmation(false);
            usingTemplates.operations.loadTemplates();
          }
        }}
      />
      <EeDrawer
        open={usingTemplates.models.openKPIDrawer}
        onClose={() => setOpenCloseConfirmation(true)}
      >
        {usingTemplates.models.openKPIDrawer && (
          <KPITemplatesDrawer
            mode={mode}
            onClose={() => setOpenCloseConfirmation(true)}
            onSave={handleSave}
            typeOptions={usingLibrary.models.types}
            groupOptions={usingLibrary.models.groups.map(({ name }) => name)}
            groupedKPIs={usingLibrary.models.groupedKPIs}
          />
        )}
      </EeDrawer>
      <EeModal
        open={openCloseConfirmation}
        title={`Close ${DRAWER_MODE_TITLES[mode]}`}
        submitTitle="Close Without Saving"
        onClose={() => setOpenCloseConfirmation(false)}
        onSubmit={() => {
          setOpenCloseConfirmation(false);
          usingTemplates.operations.closeKPIDrawer();
        }}
      >
        <p className="p-3 mb-0">
          Do you want to close the <strong>{DRAWER_MODE_TITLES[mode]}</strong>{' '}
          window without saving? Any changes made will not be saved.
        </p>
      </EeModal>
    </div>
  );
});
